import React, { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useNavigate } from "react-router-dom";
import { FaClock } from "react-icons/fa";
import { BiBookReader } from "react-icons/bi";
import { GiCalculator } from "react-icons/gi";
import "../CaseResult/Case.css"; // Include your CSS file
import Go from "../Go/Go";
import Entry from "../Entry/Entry";

gsap.registerPlugin(ScrollTrigger);

const Case = () => {
  const cards = [
    {
      id: 1,
      category: "Business Law",
      title: "Corporate Tax Compliance",
      description:
        "Expert solutions for business tax filing, compliance, and audits. Providing tailored tax strategies for businesses to ensure proper tax planning.",
      image: "/cd.webp",
      categoryClass: "category__01-cc",
      hearings: 8,
    },
    {
      id: 2,
      category: "Taxation",
      title: "Tax Audits & Disputes",
      description:
        "Professional representation in tax audits, handling disputes with tax authorities, ensuring compliance, and minimizing penalties.",
      image: "/cc.webp",
      categoryClass: "category__02-cc",
      hearings: 10,
    },
    {
      id: 3,
      category: "Financial Planning",
      title: "Wealth Management & Tax Planning",
      description:
        "Specialized in wealth management, tax-saving strategies, and retirement planning. Providing comprehensive financial services for individuals.",
      image: "/dc.webp",
      categoryClass: "category__03-cc",
      hearings: 5,
    },
    {
      id: 4,
      category: "Corporate Finance",
      title: "Mergers & Acquisitions",
      description:
        "Focused on providing expert advice and handling mergers and acquisitions, ensuring smooth financial transitions and compliance with regulations.",
      image: "/pi.webp",
      categoryClass: "category__04-cc",
      hearings: 7,
    },
    {
      id: 5,
      category: "Personal Finance",
      title: "Personal Tax Filing",
      description:
        "Providing tax filing services for individuals, ensuring compliance with tax laws, and maximizing deductions to minimize tax liabilities.",
      image: "/dv.webp",
      categoryClass: "category__05-cc",
      hearings: 6,
    },
    {
      id: 6,
      category: "Accounting Services",
      title: "Bookkeeping & Accounting",
      description:
        "Expert bookkeeping and accounting services for businesses, ensuring accurate financial records, compliance, and financial reporting.",
      image: "/aa.webp",
      categoryClass: "category__03-cc",
      hearings: 4,
    },
  ];

  const [selectedCategory, setSelectedCategory] = React.useState("All");
  const navigate = useNavigate();

  const filteredCards =
    selectedCategory === "All"
      ? cards
      : cards.filter((card) => card.category === selectedCategory);

  const categories = [
    "All",
    "Taxation",
    "Financial Planning",
    "Corporate Finance",
    "Personal Finance",
    "Accounting Services",
  ];

  const handleReadMore = (id) => {
    navigate(`/results/${id}`);
  };

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const isTablet = window.matchMedia("(max-width: 1024px)").matches;

    gsap.utils.toArray(".thumbnail-cc img").forEach((image, index) => {
      const initialRadius =
        isMobile || isTablet
          ? "0%"
          : index % 2 === 0
            ? "0% 0 0 0%"
            : "0 0% 0% 0";
      const finalRadius =
        isMobile || isTablet
          ? "0%"
          : index % 2 === 0
            ? "0 4rem 4rem 0"
            : "4rem 0 0 4rem";

      gsap.fromTo(
        image,
        { borderRadius: initialRadius },
        {
          borderRadius: finalRadius,
          scrollTrigger: {
            trigger: image,
            start: "top 80%",
            end: "bottom top",
            scrub: true,
            toggleActions: "play reverse play reverse",
          },
          duration: isMobile ? 1 : 1.5,
        },
      );
    });

    gsap.utils.toArray(".card-cc").forEach((card) => {
      const image = card.querySelector(".thumbnail-cc img");
      const content = card.querySelector(".card-content-cc");
      const title = card.querySelector(".ctc");

      gsap.fromTo(
        image,
        {
          scale: 1,
          opacity: 0.8,
          x: isMobile ? "0%" : "-10%",
        },
        {
          scale: isMobile || isTablet ? 1.6 : 2,
          opacity: 1,
          x: "0%",
          scrollTrigger: {
            trigger: card,
            start: "top 80%",
            end: "bottom top",
            scrub: true,
            toggleActions: "play reverse play reverse",
          },
        },
      );

      gsap.fromTo(
        content,
        {
          opacity: 0,
          y: isMobile ? 10 : 30,
          scale: 1,
          x: isMobile ? "0%" : "10%",
        },
        {
          opacity: 1.5,
          y: 0,
          scale: 1,
          x: "0%",
          fontSize: isMobile ? "1.5rem" : "3rem",
          scrollTrigger: {
            trigger: card,
            start: "top 80%",
            end: "bottom top",
            scrub: true,
            toggleActions: "play reverse play reverse",
          },
        },
      );

      gsap.fromTo(
        title,
        {
          fontSize: isMobile ? "1rem" : "1.3rem",
          opacity: 0.8,
        },
        {
          fontSize: isMobile ? "1.5rem" : "3rem",
          opacity: 1,
          scrollTrigger: {
            trigger: card,
            start: "top 80%",
            end: "bottom top",
            scrub: true,
            toggleActions: "play reverse play reverse",
          },
        },
      );

      gsap.fromTo(
        ".ctd",
        {
          fontSize: isMobile ? "1rem" : "1.1rem",
          opacity: 0.8,
        },
        {
          fontSize: isMobile ? "1rem" : "1.5rem",
          opacity: 1,
          scrollTrigger: {
            trigger: ".card-content-cc",
            start: "top 80%",
            end: "bottom top",
            scrub: true,
            toggleActions: "play reverse play reverse",
          },
        },
      );
    });

    gsap.to(".main-heading-pcs", {
      scale: isMobile || isTablet ? 1.5 : 2,
      duration: 2,
      ease: "power2.out",
      delay: 1.5,
      scrollTrigger: {
        trigger: ".full-service",
        start: "top center",
        end: "bottom center",
        scrub: 1,
        toggleActions: "restart reverse restart reverse",
      },
    });
  }, []);

  return (
    <>
      <Entry />

      <div className="full-service">
        <div className="decorated-heading-pcs">
          <GiCalculator className="react-icon-pcs" />
        </div>
        <h1 className="main-heading-pcs">Our Financial Success</h1>
        <p className="description-pcs">
          Navigating financial complexities can be overwhelming. Our experienced
          Chartered Accountants are here to provide you with expert financial
          guidance and support. We specialize in the following practice areas to
          help you achieve financial stability and success.
        </p>

        <div className="case-container-cc">
          {/* Toggle Buttons for Categories */}
          <div className="toggle-buttons-cc">
            {categories.map((category) => (
              <button
                key={category}
                className={`toggle-button-cc ${selectedCategory === category ? "active-cc" : ""}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Cards Section */}
          <section className="cards-cc">
            {filteredCards.map((card) => (
              <article className="card-cc" key={card.id}>
                <picture className="thumbnail-cc">
                  <img
                    className={card.categoryClass}
                    src={card.image}
                    alt={card.title}
                  />
                </picture>
                <div className="card-content-cc">
                  <h2 className="ctc">{card.title}</h2>
                  <p className="ctd">{card.description}</p>
                  <footer className="footer-cc">
                    <div className="post-meta-cc">
                      <span className="timestamp-cc">
                        <FaClock /> 6 mins ago
                      </span>
                      <span className="hearings-cc">
                        <BiBookReader /> {card.hearings} years
                      </span>
                    </div>
                    <button
                      className="read-more-cc"
                      onClick={() => handleReadMore(card.id)}
                    >
                      Read More
                    </button>
                  </footer>
                </div>
              </article>
            ))}
          </section>
        </div>
      </div>
      <Go />
    </>
  );
};

export default Case;
